import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import RichTextEditor from '../../components/editor/RichTextEditor';
import {
  mergeHelpCenterDetailsWithLanguages,
  stripHelpCenterDetails,
} from './helpCenterConstants';

const EMPTY_FORM = {
  sort: 1,
  details: [],
};

export default function HelpCenterEditModal({ open, helpCenterId, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(helpCenterId);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const langList = await adminApi.newsLanguages();
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        if (helpCenterId) {
          const data = await adminApi.getHelpCenter(helpCenterId);
          if (cancelled) {
            return;
          }
          setForm({
            sort: data.sort ?? 1,
            details: mergeHelpCenterDetailsWithLanguages(langList, data.details),
          });
        } else {
          setForm({
            ...EMPTY_FORM,
            details: mergeHelpCenterDetailsWithLanguages(langList, []),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open, helpCenterId]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  function handleReset() {
    setError('');
    if (isEdit) {
      setForm((prev) => ({
        ...prev,
        details: prev.details.map((detail) => ({ ...detail, title: '', body: '' })),
      }));
      return;
    }
    setForm({
      ...EMPTY_FORM,
      details: mergeHelpCenterDetailsWithLanguages(languages, []),
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const details = stripHelpCenterDetails(form.details).filter((d) => d.title?.trim());
    if (!details.length) {
      setError('至少填寫一個語系的標題');
      return;
    }
    const payload = {
      sort: Number(form.sort),
      details,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.updateHelpCenter({ id: helpCenterId, ...payload });
      } else {
        await adminApi.createHelpCenter(payload);
      }
      onSaved?.(isEdit);
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>{isEdit ? '修改' : '新增'}</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <p className="table-empty">載入中…</p>
        ) : (
          <form className="modal-panel__body announcement-center-edit-form" onSubmit={handleSubmit}>
            {error ? <p className="form-message form-message--error">{error}</p> : null}

            <div className="form-field">
              <label htmlFor="helpCenterSort">
                <span className="form-label-required">顯示順序</span>
              </label>
              <input
                id="helpCenterSort"
                type="number"
                min="0"
                value={form.sort}
                onChange={(e) => setForm((p) => ({ ...p, sort: e.target.value }))}
                required
              />
            </div>

            {form.details.map((detail, index) => (
              <section key={detail.lang} className="announcement-center-lang-section">
                <div className="form-field">
                  <label htmlFor={`help-center-title-${detail.lang}`}>
                    <span className="form-label-required">標題（{detail._label || detail.lang}）</span>
                  </label>
                  <input
                    id={`help-center-title-${detail.lang}`}
                    type="text"
                    value={detail.title || ''}
                    onChange={(e) => updateDetail(index, 'title', e.target.value)}
                  />
                </div>

                <div className="form-field form-field--full">
                  <span className="form-label">內容（{detail._label || detail.lang}）</span>
                  <RichTextEditor
                    value={detail.body || ''}
                    onChange={(value) => updateDetail(index, 'body', value)}
                  />
                </div>
              </section>
            ))}

            <div className="modal-panel__footer admin-modal__footer--center">
              <button type="submit" className="btn btn-success" disabled={submitting}>
                {submitting ? '提交中…' : '提交'}
              </button>
              <button type="button" className="btn btn-warning" onClick={handleReset} disabled={submitting}>
                重置
              </button>
              <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
                關閉
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
